import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  MenuItem,
  Chip,
} from '@mui/material';
import { Calendar, Plus, Edit2, Trash2 } from 'lucide-react';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { TiptapEditor } from '../components/TiptapEditor';

const EventManager: React.FC = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_type: 'MEETING',
  });

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data || []);
    } catch (err) {
      console.error('Failed to fetch events', err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleOpen = (event: any = null) => {
    if (event) {
      setEditingId(event.id);
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_date: event.event_date ? event.event_date.split('T')[0] : '',
        event_type: event.event_type || 'MEETING',
      });
    } else {
      setEditingId(null);
      setFormData({ title: '', description: '', event_date: '', event_type: 'MEETING' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.event_date) {
      alert('กรุณากรอกชื่อกิจกรรมและวันที่');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, formData);
      } else {
        await api.post('/events', formData);
      }
      handleClose();
      fetchEvents();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกกิจกรรม');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบกำหนดการ?')) {
      try {
        await api.delete(`/events/${id}`);
        fetchEvents();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'DEADLINE':
        return <Chip label="วันสิ้นสุด/ส่งงาน" size="small" sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 600 }} />;
      case 'MEETING':
        return <Chip label="วันประชุม/นิเทศก์" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 600 }} />;
      case 'HOLIDAY':
        return <Chip label="วันหยุด" size="small" sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 600 }} />;
      default:
        return <Chip label="อื่นๆ" size="small" sx={{ bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />;
    }
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Calendar size={28} color="#1E40AF" />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>
              ปฏิทินและกำหนดการฝึกประสบการณ์วิชาชีพครู
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            จัดการกำหนดการสำคัญ วันสิ้นสุดส่งงาน และวันนัดหมายนิเทศก์
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={18} />}
          onClick={() => handleOpen()}
          sx={{
            fontWeight: 600,
            borderRadius: 2.5,
            px: 2.5,
            py: 1,
            boxShadow: '0 4px 14px rgba(30, 64, 175, 0.25)',
          }}
        >
          เพิ่มกิจกรรมใหม่
        </Button>
      </Box>

      {/* Events Table */}
      <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>วันที่</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 220 }}>กิจกรรม</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 160 }}>ประเภท</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>รายละเอียด</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, width: 120 }}>การจัดการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event: any) => (
                <TableRow key={event.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                    {new Date(event.event_date).toLocaleDateString('th-TH', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1E3A8A' }}>{event.title}</TableCell>
                  <TableCell>{getTypeBadge(event.event_type)}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        fontSize: '0.88rem',
                        color: '#475569',
                        '& p': { m: 0 },
                      }}
                      dangerouslySetInnerHTML={{ __html: event.description || '-' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpen(event)}
                        sx={{ bgcolor: '#EFF6FF' }}
                      >
                        <Edit2 size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(event.id)}
                        sx={{ bgcolor: '#FEF2F2' }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5, color: '#64748B' }}>
                    ยังไม่มีข้อมูลกำหนดการในระบบ
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </GlassCard>

      {/* CREATE / EDIT DIALOG WITH TIPTAP */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: { sx: { borderRadius: 3, p: 1 } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E3A8A', borderBottom: '1px solid #E2E8F0', pb: 2 }}>
          {editingId ? 'แก้ไขกำหนดการ' : 'เพิ่มกำหนดการใหม่'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="ชื่อกิจกรรม / กำหนดการ *"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="เช่น ส่งรายงานการนิเทศรอบที่ 1, สัมมนาหลังฝึกสอน..."
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="วันที่ *"
                name="event_date"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                value={formData.event_date}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="ประเภทกิจกรรม"
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
              >
                <MenuItem value="DEADLINE">วันสิ้นสุด/ส่งงาน (Deadline)</MenuItem>
                <MenuItem value="MEETING">วันประชุม/นิเทศก์ (Meeting)</MenuItem>
                <MenuItem value="HOLIDAY">วันหยุด (Holiday)</MenuItem>
                <MenuItem value="OTHER">อื่นๆ (Other)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TiptapEditor
                label="รายละเอียดกิจกรรมและกำหนดการ (Rich Text)"
                value={formData.description}
                onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                placeholder="ระบุวาระการประชุม รายละเอียดงาน หรือสถานที่..."
                minHeight={150}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ px: 3, borderRadius: 2 }}>
            {editingId ? 'บันทึกการแก้ไข' : 'บันทึกกิจกรรม'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventManager;
